import { INodePropCfg, getNodeProps } from '@intelligence/utils/domUtils';

/**
 * 工作表名称验证结果
 */
export interface ISheetNameValidation {
  /** 是否有效 */
  valid: boolean;
  /** 错误消息 key（如果无效） */
  messageKey?: string;
  /** 错误消息（如果无效）- 旧版兼容 */
  message?: string;
}

/**
 * 非法字符列表：\ / ? * [ ]
 */
const INVALID_CHARS = /[\\/?*[\]]/;

/**
 * 工作表名称最大长度
 */
const MAX_SHEET_NAME_LENGTH = 30;

/**
 * 默认空白工作表名称 i18n key
 */
export const BLANK_SHEET_NAME_KEY = 'dba_smart_table_blank_sheet';

/**
 * 默认空白工作表名称（fallback）
 */
const DEFAULT_BLANK_SHEET_NAME = 'Sheet';

/**
 * 验证工作表名称是否合法
 * 规则：
 * 1. 名称不能为空
 * 2. 名称不能超过31个字符
 * 3. 名称中不能包含以下字符: \ / ? * [ ]
 * 4. 名称的第一个或最后一个字符不能是单引号 '
 * 
 * @param name 要验证的名称
 * @returns 验证结果（包含 messageKey 用于国际化）
 */
export const validateSheetName = (name: string): ISheetNameValidation => {
  const trimmedName = name.trim();

  // 1. 检查是否为空
  if (!trimmedName || trimmedName.length === 0) {
    return { valid: false, messageKey: 'dba_smart_table_sheet_name_empty' };
  }

  // 2. 检查长度是否超过31个字符
  if (trimmedName.length > MAX_SHEET_NAME_LENGTH) {
    return { valid: false, messageKey: 'dba_smart_table_sheet_name_length_error' };
  }

  // 3. 检查是否包含非法字符
  if (INVALID_CHARS.test(trimmedName)) {
    return { valid: false, messageKey: 'dba_smart_table_sheet_name_char_error' };
  }

  // 4. 检查首尾是否为单引号
  if (trimmedName.startsWith("'") || trimmedName.endsWith("'")) {
    return { valid: false, messageKey: 'dba_smart_table_sheet_name_quote_error' };
  }

  return { valid: true };
};

/**
 * 检查工作表名称是否重复，如果重复则生成唯一名称
 * 命名规则：重名加 -1, -2, -3 ...
 * 
 * @param name 原始名称
 * @param existingNames 已存在的名称列表
 * @param excludeId 排除的工作表ID（用于重命名时排除自身）
 * @returns 唯一的名称
 */
export const getUniqueSheetName = (
  name: string,
  existingNames: string[],
  currentName?: string
): string => {
  const trimmedName = name.trim();
  
  // 过滤掉当前工作表自身的名称
  const otherNames = currentName 
    ? existingNames.filter(n => n !== currentName)
    : existingNames;

  // 如果名称不重复，直接返回
  if (!otherNames.includes(trimmedName)) {
    return trimmedName;
  }

  // 生成唯一名称：原名称-1, 原名称-2, ...
  let index = 1;
  let newName = `${trimmedName}-${index}`;
  
  while (otherNames.includes(newName)) {
    // eslint-disable-next-line no-plusplus
    index++;
    newName = `${trimmedName}-${index}`;
    
    // 检查新名称是否超过最大长度
    if (newName.length > MAX_SHEET_NAME_LENGTH) {
      // 如果超过长度，截断原名称
      const maxBaseLength = MAX_SHEET_NAME_LENGTH - `-${index}`.length;
      const truncatedBase = trimmedName.slice(0, maxBaseLength);
      newName = `${truncatedBase}-${index}`;
    }
  }

  return newName;
};

/**
 * 生成副本名称（基于当前名称递归命名）
 * 命名规则：
 * - 从 "表格1" 创建副本 -> "表格1 (1)", "表格1 (2)", ...
 * - 从 "表格1 (1)" 创建副本 -> "表格1 (1) (1)", "表格1 (1) (2)", ...
 * - 从 "表格1 (1) (1)" 创建副本 -> "表格1 (1) (1) (1)", ...
 * 
 * @param baseName 当前工作表名称（作为副本的父级名称）
 * @param existingNames 已存在的名称列表
 */
export const generateCopyName = (baseName: string, existingNames: string[]): string => {
  // 直接基于当前名称创建副本，不移除已有后缀
  let copyIndex = 1;
  let newName = `${baseName} (${copyIndex})`;
  
  while (existingNames.includes(newName)) {
    // eslint-disable-next-line no-plusplus
    copyIndex++;
    newName = `${baseName} (${copyIndex})`;
  }
  
  // 检查是否超过最大长度
  if (newName.length > MAX_SHEET_NAME_LENGTH) {
    const maxBaseLength = MAX_SHEET_NAME_LENGTH - ` (${copyIndex})`.length;
    const truncatedBase = baseName.slice(0, maxBaseLength);
    newName = `${truncatedBase} (${copyIndex})`;
  }
  
  return newName;
};

/**
 * 生成新工作表名称（空白工作表递增）
 * @param existingNames 已存在的名称列表
 * @param baseName 基础名称（国际化后的空白工作表名称）
 */
export const generateNewSheetName = (existingNames: string[], baseName: string = DEFAULT_BLANK_SHEET_NAME): string => {
  // 检查是否存在 "空白工作表"
  if (!existingNames.includes(baseName)) {
    return baseName;
  }
  
  let index = 1;
  let newName = `${baseName}${index}`;
  
  while (existingNames.includes(newName)) {
    // eslint-disable-next-line no-plusplus
    index++;
    newName = `${baseName}${index}`;
  }
  
  return newName;
};

// ===== 以下函数用于 useSlideEffect hook =====

const childProps: INodePropCfg[] = [
  { name: 'width', type: 'number' },
  { name: 'padding-left', type: 'number' },
  { name: 'padding-right', type: 'number' },
  { name: 'margin-left', type: 'number' },
  { name: 'margin-right', type: 'number' },
];

export const getChildrenWidth = (node?: HTMLElement | null): number => {
  const children = node?.children;
  if (!children?.length) return 0;
  let totalWidth = 0;
  for (const child of children) {
    const {
      width,
      'margin-left': marginLeft,
      'margin-right': marginRight,
      'padding-left': paddingLeft,
      'padding-right': paddingRight,
    } = getNodeProps(child, childProps);
    totalWidth =
      totalWidth +
      (width as number) +
      (marginLeft as number) +
      (marginRight as number) +
      (paddingLeft as number) +
      (paddingRight as number);
  }
  return totalWidth;
};

export const getIsOverflow = (containerWidth: number, listWidth: number, offset = 0): boolean => {
  if (offset) return listWidth - containerWidth > offset;
  return listWidth > containerWidth;
};
